// patient-app/src/hooks/useVideoSession.js
//
// Manages the full lifecycle of a patient's video session:
//   idle → joining → waiting → in-call → ended
//
// The patient joins the Chime meeting immediately on lobby entry (muted,
// no video). The hook polls the session status every 4 seconds. When the
// doctor joins, status transitions to IN_PROGRESS and the hook moves the
// UI into "in-call" mode — activating audio and video.

import { useState, useCallback, useRef, useEffect } from "react";
import { useMeetingManager } from "amazon-chime-sdk-component-library-react";
import { MeetingSessionConfiguration } from "amazon-chime-sdk-js";

const API            = 'https://pzfesc4g10.execute-api.us-east-1.amazonaws.com/prod';
const POLL_INTERVAL  = 4_000; // ms

export function useVideoSession() {
  const meetingManager = useMeetingManager();

  const [status,  setStatus]  = useState("idle");   // idle|joining|waiting|in-call|ended
  const [session, setSession] = useState(null);
  const [error,   setError]   = useState(null);

  const sessionIdRef = useRef(null);
  const pollRef      = useRef(null);

  // ── Cleanup on unmount ─────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      clearInterval(pollRef.current);
    };
  }, []);

  // ── Poll session status while in the waiting room ─────────────────────
  const startPolling = useCallback((sessionId) => {
    pollRef.current = setInterval(async () => {
      try {
        const res  = await fetch(`${API}/sessions/${sessionId}`);
        if (!res.ok) return;
        const data = await res.json();

        if (data.status === "IN_PROGRESS") {
          clearInterval(pollRef.current);
          setStatus("in-call");
        } else if (data.status === "ENDED") {
          clearInterval(pollRef.current);
          await meetingManager.leave();
          setStatus("ended");
        }
      } catch (_) {
        // network blip — retry on next tick
      }
    }, POLL_INTERVAL);
  }, [meetingManager]);

  // ── Join the lobby ─────────────────────────────────────────────────────
  const joinLobby = useCallback(async (sessionId) => {
    setStatus("joining");
    setError(null);
    sessionIdRef.current = sessionId;

    try {
      const res  = await fetch(`${API}/sessions/join`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ sessionId, role: "PATIENT" }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "Failed to join session");

      setSession(data.session);

      // Build MeetingSessionConfiguration from the API response.
      // MeetingSessionConfiguration accepts either:
      //   { Meeting: { MeetingId, MediaPlacement, ... } }  +  { Attendee: { AttendeeId, JoinToken, ... } }
      // OR the flat versions — both work; the SDK normalises internally.
      const configuration = new MeetingSessionConfiguration(
        data.meeting,   // backend returns { Meeting: { ... } }
        data.attendee,  // backend returns { Attendee: { ... } }
      );

      // Join the Chime meeting (audio/video inactive until doctor admits)
      await meetingManager.join(configuration);

      // Select default devices (mic + camera) — required before start()
      await meetingManager.listAndSelectDevices();

      // Start audio/video session
      await meetingManager.start();

      setStatus("waiting");

      // Begin polling for doctor arrival
      startPolling(sessionId);
    } catch (err) {
      setError(err.message);
      setStatus("idle");
    }
  }, [meetingManager, startPolling]);

  // ── Leave / end call ───────────────────────────────────────────────────
  const leaveCall = useCallback(async () => {
    clearInterval(pollRef.current);
    try {
      await meetingManager.leave();
    } catch (_) { /* already left */ }
    setStatus("ended");
  }, [meetingManager]);

  return { session, status, error, joinLobby, leaveCall };
}
